import { FC, useEffect, useState } from "react";
import BotLogo from "../../../assets/images/bot-logo.svg";
import { Box, FormControl, Stack, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import TextInput from "../../../components/TextInput";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { CustomButton } from "../../../components/Button";
import { adjustToggle } from "../../../utils/adjustToggle";
import { useForm } from "react-hook-form";
import { errorToast, successToast } from "../../../utils/toast";
import {
  useForgotPasswordMutation,
  useResetPasswordMutation,
} from "../../../services/auth";
import "./index.css";
import { Link, useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { ReactComponent as LockIcon } from "../../../assets/images/lock.svg";
import { ReactComponent as EmailIcon } from "../../../assets/images/mail-secure-02.svg";
import AirHorn from "../../../assets/images/air-horn 1.png";
import Celebrate from "../../../assets/images/celebrate.gif";

const currentYear = new Date().getFullYear();

const ForgotPassword: FC = () => {
  const [forgotPassword, result] = useForgotPasswordMutation();
  const [resetPassword, resetResult] = useResetPasswordMutation();
  const [loader, setLoader] = useState(false);
  const [otp, setOtp] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const theme = useTheme();
  const mode = theme.palette.mode;
  const navigate = useNavigate();
  const [passwordCheckEnable, setPasswordCheckEnable] = useState(false);
  const passwordConditions = [
    { text: "8 characters long", regex: /^.{8,}$/ },
    { text: "1 uppercase letter (A–Z)", regex: /[A-Z]/ },
    { text: "1 lowercase letter (a–z)", regex: /[a-z]/ },
    { text: "1 number", regex: /\d/ },
    { text: "1 special character e.g !@#$%^&*()", regex: /[!@#$%^&*()]/ },
  ];
  useEffect(() => {
    adjustToggle();
  }, []);

  const {
    register,
    resetField,
    reset,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm();

  const {
    register: otpRegister,
    resetField: otpResetField,
    reset: resetOtp,
    handleSubmit: otpHandleSubmit,
    watch,
    getValues: getOtpValues,
    formState: { errors: otpErrors },
  } = useForm();

  const onSubmit = async (data: any) => {
    setLoader(true);
    const { username } = data;
    const body = {
      username,
    };
    const response: any = await forgotPassword(body);
    setLoader(false);
    if (response.error) {
      errorToast(
        response.message ||
          response.error.error ||
          (response.error.data && response.error.data.message)
      );
    } else {
      successToast(response.data.message);
    }
  };

  useEffect(() => {
    const password = watch("newPassword");
    const confirmPassword = watch("confirmPassword");
    if (
      password &&
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()]).{8,}$/.test(
        password
      ) &&
      confirmPassword &&
      confirmPassword == password
    ) {
      setPasswordCheckEnable(true);
    } else {
      setPasswordCheckEnable(false);
    }
  }, [watch("newPassword"), watch("confirmPassword")]);

  const passwordCheck = (condition: string, regex: any) => {
    const password = watch("newPassword");
    return (
      <Typography component="div"
        fontSize={"12px"}
        textAlign={"left"}
        color={
          password && !regex.test(password) ? "#e11c1cb3 !important" : "grey"
        }
      >
        {condition}
      </Typography>
    );
  };

  const onOtpSubmit = async (data: any) => {
    setLoader(true);
    const body = {
      username: getValues().username,
      newPassword: data.newPassword,
      confirmationCode: otp.join(""),
    };
    const response: any = await resetPassword(body);
    setLoader(false);
    if (response.error) {
      [1, 2, 3, 4, 5, 6].forEach((n) => {
        otpResetField(`otp-${n}`);
      });
      setOtp([]);
      errorToast(
        response.message ||
          response.error.error ||
          (response.error.data && response.error.data.message)
      );
    } else {
      const elem = document.getElementById("root");
      if (elem) {
        elem.style.backgroundImage = `url(${Celebrate})`;
      }
      resetOtp();
      setOtp([]);
    }
  };

  const handleVisibility = () => {
    setShowPassword(!showPassword);
  };
  const handleConfirmVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  useEffect(() => {
    document.addEventListener("paste", (event: ClipboardEvent) => {
      const clipboardData: any = event.clipboardData;
      const copiedOtp = clipboardData.getData("text/plain");
      copiedOtp.split("").forEach((v: any, i: number) => {
        const elem: any = document.getElementById(`otp-${i + 1}`);
        if (elem) {
          elem.value = v;
        }
      });
    });
  }, []);

  const showOtpCodeFields = () => {
    return (
      <Stack direction={"row"} spacing={2}>
        {[1, 2, 3, 4, 5, 6].map((val, ind) => {
          return (
            <FormControl variant="standard" key={ind}>
              <TextInput
                id={`otp-${val}`}
                register={otpRegister}
                name={`otp-${val}`}
                handleKeyUp={(e) => {
                  let tempOtp = otp;
                  tempOtp[ind] = e.key;
                  setOtp(tempOtp);
                  if ((tempOtp[ind] || tempOtp[ind] == "0") && e.which != 8) {
                    document.getElementById(`otp-${val + 1}`)?.focus();
                  }
                }}
                textAlign="center"
                maxLength={1}
                required={true}
                borderColor={otpErrors[`otp-${val}`] && "#e11c1cb3"}
              />
            </FormControl>
          );
        })}
      </Stack>
    );
  };

  return (
    <>
      {!resetResult.isSuccess ? (
        <Box
          sx={{
            flex: 1,
          }}
        >
          <img src={BotLogo} alt="Logo" className="bot-logo" />
          <Grid
            container
            spacing={2}
            direction="column"
            alignItems="center"
            justifyContent="center"
            height={"100%"}
          >
            <Grid size={{ xs: 10, md: 4 }} textAlign={"center"}>
              <div>
                {!result.isSuccess ? (
                  <LockIcon
                    className={mode == "dark" ? "light-lock" : "dark-lock"}
                  />
                ) : (
                  <EmailIcon
                    className={
                      theme.palette.mode == "dark"
                        ? "light-email"
                        : "dark-email"
                    }
                  />
                )}
                <div
                  className={
                    mode == "dark" ? "forgot-title-d" : "forgot-title-l"
                  }
                >
                  {result.isSuccess
                    ? `Verify OTP & Create a new password`
                    : `Forgot your password?`}
                </div>
                {!result.isSuccess ? (
                  <Typography component="div" margin={"20px 0px"}>
                    Just enter the email you signed up with and we’ll send you a
                    6-digit OTP code.
                  </Typography>
                ) : null}
                <form
                  onSubmit={
                    result.isSuccess
                      ? otpHandleSubmit(onOtpSubmit)
                      : handleSubmit(onSubmit)
                  }
                >
                  <Stack spacing={2}>
                    {result.isSuccess ? (
                      <>
                        {showOtpCodeFields()}
                        <Typography component="div" className="text">
                          Didn't receive code?{" "}
                          <div
                            className="signin"
                            onClick={() =>
                              onSubmit({ username: getValues().username })
                            }
                          >
                            Resend OTP
                          </div>
                        </Typography>
                        <FormControl variant="standard">
                          <TextInput
                            label="New password"
                            placeholder="Enter your new password"
                            ariaLabel="email"
                            iconHandler={handleVisibility}
                            icon={
                              showPassword ? (
                                <VisibilityOffIcon
                                  sx={{ color: "#2A945F", fontSize: "20px" }}
                                />
                              ) : (
                                <VisibilityIcon
                                  sx={{ color: "#2A945F", fontSize: "20px" }}
                                />
                              )
                            }
                            borderColor={otpErrors.newPassword && "#e11c1cb3"}
                            register={otpRegister}
                            required={true}
                            pattern={
                              /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,16}$/
                            }
                            type={showPassword ? "text" : "password"}
                            name="newPassword"
                            helperText={
                              otpErrors.newPassword
                                ? otpErrors.newPassword.type == "pattern"
                                  ? `Password must contain at least 8 characters, at least 1 uppercase letter (A-X), at least 1 lowercase letter (a-z), at least 1 number (0-9) and at least 1 special character from this set: !@#$%^&*()-_=+[]{}|;:'",.<>?/.`
                                  : "Required field"
                                : ""
                            }
                          />
                          <br />
                          <TextInput
                            label="Confirm password"
                            placeholder="Confirm your new password"
                            ariaLabel="email"
                            iconHandler={handleConfirmVisibility}
                            icon={
                              showConfirmPassword ? (
                                <VisibilityOffIcon
                                  sx={{ color: "#2A945F", fontSize: "20px" }}
                                />
                              ) : (
                                <VisibilityIcon
                                  sx={{ color: "#2A945F", fontSize: "20px" }}
                                />
                              )
                            }
                            borderColor={
                              watch("confirmPassword") &&
                              watch("newPassword") != watch("confirmPassword")
                                ? "#e11c1cb3"
                                : ""
                            }
                            register={otpRegister}
                            required={true}
                            pattern={
                              /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,16}$/
                            }
                            type={showConfirmPassword ? "text" : "password"}
                            name="confirmPassword"
                            helperText={
                              watch("confirmPassword") &&
                              watch("newPassword") != watch("confirmPassword")
                                ? "Passwords do not match"
                                : ""
                            }
                          />
                        </FormControl>
                      </>
                    ) : (
                      <FormControl variant="standard">
                        <TextInput
                          label="Email Address or username"
                          placeholder="Enter a valid email address or username"
                          ariaLabel="email"
                          iconHandler={() => resetField("username")}
                          icon={
                            <HighlightOffIcon
                              sx={{ color: "#2A945F", fontSize: "20px" }}
                            />
                          }
                          borderColor={errors.username && "#e11c1cb3"}
                          register={register}
                          required={true}
                          name="username"
                        />
                      </FormControl>
                    )}
                    {result.isSuccess ? (
                      <>
                        <Typography component="div"
                          fontSize={"12px"}
                          textAlign={"left"}
                          color={"grey"}
                        >
                          Your password must contain:
                          {passwordConditions.map((condition) =>
                            passwordCheck(condition.text, condition.regex)
                          )}
                        </Typography>
                      </>
                    ) : null}
                    <CustomButton
                      text={
                        loader
                          ? ""
                          : result.isSuccess
                          ? "Reset password"
                          : "Send link"
                      }
                      disabled={result.isSuccess && !passwordCheckEnable}
                    />
                    <Typography component="div" className="text">
                      Remember your password?{" "}
                      <Link className="signin" to="/signin">
                        Sign in
                      </Link>
                    </Typography>
                  </Stack>
                </form>
              </div>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            marginTop: "30px",
          }}
        >
          <img src={BotLogo} alt="Logo" className="bot-logo" />
          <Grid container spacing={2} direction="column" alignItems="center">
            <img src={AirHorn} style={{ width: "230px", height: "230px" }} />
            <Typography component="div" color="#111">Password updated</Typography>
            <CustomButton
              bgcolor="#30A46C !important"
              width="200px"
              text="Login to HiiBo"
              submit={() => {
                const elem = document.getElementById("root");
                if (elem) {
                  elem.style.backgroundImage = "";
                }
                navigate("/signin");
              }}
            />
          </Grid>
        </Box>
      )}
      <div className="copyright-div">
        <Typography component="div" className="copyright">
          © {currentYear} ALL RIGHTS RESERVED
        </Typography>
      </div>
    </>
  );
};

export default ForgotPassword;
